import type { OptionSegmented } from '@flash-global66/g-segmented';

import type {
  CredentialApiCard,
  CredentialApiDocLink,
  CredentialApiSecurityCard,
  CredentialApiStatus,
  CredentialApiStatusMeta,
} from '@/modules/credentialApi/types/credentialApi.type';

export const CREDENTIAL_API_DOCS_URL =
  'https://documents-b2b.global66.com/transactional-api/introduction/';
export const CREDENTIAL_API_DESTINATIONS_URL =
  'https://documents-b2b.global66.com/transactional-api/destinations/';

export const CREDENTIAL_API_INTRO_HELP_EMAIL = 'soporte.tech@global66.com';
export const CREDENTIAL_API_INTRO_HELP_MAILTO = `mailto:${CREDENTIAL_API_INTRO_HELP_EMAIL}`;

export const CREDENTIAL_API_RESOURCE_CARDS: ReadonlyArray<CredentialApiCard> = [
  {
    title: 'Developer Portal',
    description:
      'Accede a nuestra documentación técnica, guía de integración y simulador de request para realizar pruebas.',
    icon: 'solid book',
    url: CREDENTIAL_API_DOCS_URL,
    linkLabel: 'Ver documentación',
  },
  {
    title: 'Playbook de Destinos',
    description:
      'Revisa en tiempo real nuestra cobertura, SLAs y campos requeridos por país.',
    icon: 'solid file-alt',
    url: CREDENTIAL_API_DESTINATIONS_URL,
    linkLabel: 'Ver destinos',
  },
];

export const CREDENTIAL_API_SECURITY_CARDS: ReadonlyArray<CredentialApiSecurityCard> = [
  {
    title: 'Operaciones en vivo',
    description:
      'Crea credenciales para ejecutar transacciones reales y automatizar la operativa financiera de tu empresa de forma programática.',
  },
  {
    title: 'IP Whitelist',
    description:
      'Refuerza tu seguridad permitiendo que la API solo procese solicitudes provenientes de las direcciones IP de confianza que tú definas.',
  },
];

export const CREDENTIAL_API_PAGE_TITLE = 'API Transaccional';
export const CREDENTIAL_API_PAGE_SUBTITLE =
  'Automatiza tus pagos globales conectando tu ERP o plataforma directamente con la infraestructura de Global66.';

export const CREDENTIAL_API_BANNER_TITLE = 'Integración API de Global66';
export const CREDENTIAL_API_BANNER_BODY =
  'Conecta tus sistemas para automatizar pagos, transferencias y consultas de saldo de forma segura mediante nuestra API.';

export const CREDENTIAL_API_EMPTY_TITLE = 'Integra tu negocio vía API';
export const CREDENTIAL_API_EMPTY_BODY =
  'Genera tus credenciales de Client ID y Client Secret para autenticar tus sistemas y automatizar tus operaciones financieras de forma segura.';
export const CREDENTIAL_API_EMPTY_BUTTON_LABEL = 'Generar';

export const CREDENTIAL_API_DATA_TITLE = 'Credenciales y Seguridad';
export const CREDENTIAL_API_DATA_BUTTON_LABEL = 'Nuevas credenciales';

/**
 * Section title for the Movements API tab. Rendered as the page's
 * section header (`<h2>`) on top of the movements table, mirroring
 * the `Credenciales y Seguridad` pattern from the credentials panel.
 * Source: SDD change `credential-api-movements` (browser QA — final
 * layout adjustment 2026-06-10).
 */
export const CREDENTIAL_API_MOVEMENTS_SECTION_TITLE = 'Movimientos API';

export const CREDENTIAL_API_PAGE_SIZE = 10;

export const CREDENTIAL_API_DEFAULT_TAB = 'credentials';

export const CREDENTIAL_API_MOVEMENTS_TAB_VALUE = 'movements';

export const CREDENTIAL_API_TABS: ReadonlyArray<OptionSegmented> = [
  { label: 'Credenciales', value: CREDENTIAL_API_DEFAULT_TAB, disabled: false },
  { label: 'Movimientos API', value: CREDENTIAL_API_MOVEMENTS_TAB_VALUE, disabled: false },
];

export const CREDENTIAL_API_DOC_LINKS: ReadonlyArray<CredentialApiDocLink> = [
  {
    label: 'Ver documentación',
    url: CREDENTIAL_API_DOCS_URL,
    icon: 'regular ballot',
  },
  {
    label: 'Destinos',
    url: CREDENTIAL_API_DESTINATIONS_URL,
    icon: 'regular game-console-handheld',
  },
];

export const CREDENTIAL_API_GENERIC_ERROR_MESSAGE =
  'No pudimos cargar tus credenciales. Por favor, intenta de nuevo.';

export const CREDENTIAL_API_CLIENT_DATE_FORMAT = 'DD/MM/YYYY HH:mm';
export const CREDENTIAL_API_TRANSACTION_DATE_FORMAT = 'DD/MM/YYYY HH:mm';
export const CREDENTIAL_API_CREATOR_PLACEHOLDER = '—';
export const CREDENTIAL_API_CLIENT_ID_MASK_PREFIX = '...';
export const CREDENTIAL_API_CLIENT_ID_MASK_SUFFIX_LENGTH = 4;

export const CREDENTIAL_API_STATUS_MAP: Record<CredentialApiStatus, CredentialApiStatusMeta> = {
  ACTIVATED: { label: 'Activo', tagType: 'success' },
  BLOCKED: { label: 'Bloqueado', tagType: 'warning' },
  DELETED: { label: 'Eliminado', tagType: 'error' },
};

export const CREDENTIAL_API_UNKNOWN_STATUS_META: CredentialApiStatusMeta = {
  label: 'Desconocido',
  tagType: 'grey',
};

export const CREDENTIAL_API_PROVISION_ENDPOINT = 'business-api/b2b/clients/provision';

export const CREDENTIAL_API_NAME_MAX_LENGTH = 50;

export const CREDENTIAL_API_CREDENTIALS_FILENAME = 'credenciales-global66.txt';

export const CREDENTIAL_API_DRAWER_FORM_TITLE = 'Generar credenciales API';
export const CREDENTIAL_API_DRAWER_FORM_DESCRIPTION =
  'Al crear esta integración, obtendrás un Client ID y un Client Secret. Estas credenciales permiten que tus sistemas externos se conecten de forma segura con la infraestructura de Global66.';

export const CREDENTIAL_API_DRAWER_NAME_LABEL = 'Nombre de la credencial';
export const CREDENTIAL_API_DRAWER_NAME_PLACEHOLDER = 'Ej. Cliente principal';
export const CREDENTIAL_API_DRAWER_NAME_HELP =
  'Asigna un nombre descriptivo para identificarla.';

export const CREDENTIAL_API_DRAWER_IPS_LABEL = 'IPs permitidas';
export const CREDENTIAL_API_DRAWER_IPS_PLACEHOLDER = 'Direcciones IP autorizadas';
export const CREDENTIAL_API_DRAWER_IPS_HELP = 'Solo IP válidas en formato IPv4.';

export const CREDENTIAL_API_DRAWER_CANCEL_BUTTON = 'Cancelar';
export const CREDENTIAL_API_DRAWER_CREATE_BUTTON = 'Crear';

export const CREDENTIAL_API_DRAWER_SUCCESS_TITLE = 'Credenciales generadas exitosamente';
export const CREDENTIAL_API_DRAWER_SUCCESS_DESCRIPTION =
  'Estas credenciales te permitirán autenticar tu aplicación y comenzar a realizar operaciones de forma programática a través de nuestra API.';

export const CREDENTIAL_API_DRAWER_WARNING_TITLE = 'Guarda tus credenciales ahora';
export const CREDENTIAL_API_DRAWER_WARNING_BODY =
  'Por tu seguridad, esta es la única vez que podrás visualizar tus credenciales. No cierras esta ventana sin copiarlas.';

export const CREDENTIAL_API_DRAWER_CLIENT_ID_LABEL = 'Client ID';
export const CREDENTIAL_API_DRAWER_CLIENT_SECRET_LABEL = 'Client Secret';

export const CREDENTIAL_API_DRAWER_COPY_CLIENT_ID_ARIA = 'Copiar Client ID';
export const CREDENTIAL_API_DRAWER_COPY_CLIENT_SECRET_ARIA = 'Copiar Client Secret';

export const CREDENTIAL_API_DRAWER_DOWNLOAD_BUTTON = 'Descargar credenciales';
export const CREDENTIAL_API_DRAWER_CLOSE_BUTTON = 'Cerrar';

export const CREDENTIAL_API_DRAWER_SUCCESS_TOAST = 'Credenciales creadas con éxito';
export const CREDENTIAL_API_DRAWER_PROVISIONING_ERROR_MESSAGE =
  'No pudimos crear las credenciales. Por favor, intenta de nuevo.';
export const CREDENTIAL_API_DRAWER_CONFLICT_ERROR_MESSAGE =
  'Ya tienes una credencial con ese nombre. Usa un nombre distinto para identificarla.';

export const CREDENTIAL_API_DRAWER_PURPOSE_UNAVAILABLE_ERROR_MESSAGE =
  'Uno de los propósitos seleccionados ya no está disponible. Revisa la selección e intenta de nuevo.';

export const CREDENTIAL_API_TRANSACTIONS_PURPOSE_CODE = 'TRANSACTIONS';
export const CREDENTIAL_API_PROVISION_ERROR_CONTEXT = 'PROVISION';
export const CREDENTIAL_API_PURPOSE_UNAVAILABLE_REASON = 'BAD_REQUEST';

export const CREDENTIAL_API_DRAWER_PURPOSES_LABEL = 'Propósito de la credencial';
export const CREDENTIAL_API_DRAWER_PURPOSES_HELP =
  'Elige para qué usarás estas credenciales. No podrás cambiarlo después.';
export const CREDENTIAL_API_DRAWER_TRANSACTIONS_WARNING_TITLE =
  'Estas credenciales pueden mover fondos';
export const CREDENTIAL_API_DRAWER_TRANSACTIONS_WARNING_BODY =
  'No las compartas con terceros que solo necesitan consultar información, como tu ERP.';
export const CREDENTIAL_API_DRAWER_PURPOSES_LOAD_ERROR_MESSAGE =
  'No pudimos cargar los propósitos. Por favor, intenta de nuevo.';
export const CREDENTIAL_API_DRAWER_PURPOSES_EMPTY_MESSAGE =
  'No encontramos propósitos disponibles para generar credenciales. Por favor, intenta de nuevo.';
export const CREDENTIAL_API_DRAWER_PURPOSES_RETRY_LABEL = 'Reintentar';

export const CREDENTIAL_API_CREDENTIALS_FILE_HEADER =
  'Credenciales API Transaccional - Global66';
export const CREDENTIAL_API_CREDENTIALS_FILE_CLIENT_ID_LABEL = 'Client ID:';
export const CREDENTIAL_API_CREDENTIALS_FILE_CLIENT_SECRET_LABEL = 'Client Secret:';

export const CREDENTIAL_API_EDIT_DRAWER_TITLE = 'Editar IPs permitidas';
export const CREDENTIAL_API_EDIT_DRAWER_DESCRIPTION =
  'Actualiza la lista de direcciones IP autorizadas para esta credencial. Los cambios se aplican de inmediato.';
export const CREDENTIAL_API_EDIT_DRAWER_NAME_LABEL = 'Nombre de la credencial';
export const CREDENTIAL_API_EDIT_DRAWER_SECRET_PREVIEW_LABEL = 'Client Secret';
export const CREDENTIAL_API_EDIT_DRAWER_IPS_LABEL = 'IPs permitidas';
export const CREDENTIAL_API_EDIT_DRAWER_IPS_PLACEHOLDER = 'Direcciones IP autorizadas';
export const CREDENTIAL_API_EDIT_DRAWER_IPS_HELP =
  'Solo IP válidas en formato IPv4.';
export const CREDENTIAL_API_EDIT_DRAWER_SAVE_BUTTON = 'Guardar';
export const CREDENTIAL_API_EDIT_DRAWER_CANCEL_BUTTON = 'Cancelar';
export const CREDENTIAL_API_EDIT_DRAWER_LOAD_ERROR_MESSAGE =
  'No pudimos cargar las IPs de la credencial. Por favor, intenta de nuevo.';
export const CREDENTIAL_API_EDIT_DRAWER_UPDATE_ERROR_MESSAGE =
  'No pudimos actualizar las IPs de la credencial. Por favor, intenta de nuevo.';
export const CREDENTIAL_API_EDIT_DRAWER_SUCCESS_TOAST = 'IPs actualizadas con éxito';

export const buildCredentialApiSecretIpsEndpoint = (params: {
  clientId: string;
  secretId: string;
}): string =>
  `business-api/b2b/clients/${params.clientId}/secrets/${params.secretId}/ips`;

export const CREDENTIAL_API_DELETE_ALERT_TITLE =
  '¿Seguro que quieres eliminar esta credencial?';
export const CREDENTIAL_API_DELETE_ALERT_PRIMARY_TEXT = 'Eliminar credencial';
export const CREDENTIAL_API_DELETE_ALERT_SECONDARY_TEXT = 'No, cancelar';
export const CREDENTIAL_API_DELETE_SUCCESS_TOAST = 'Credencial eliminada con éxito';
export const CREDENTIAL_API_DELETE_ERROR_TOAST =
  'No pudimos eliminar la credencial. Por favor, intenta de nuevo.';

export const buildCredentialApiClientEndpoint = (clientId: string): string =>
  `business-api/b2b/clients/${clientId}`;

// -----------------------------------------------------------------------------
// Block / Activate credential status actions
// Source: SDD change `credential-api-status-actions`.
// -----------------------------------------------------------------------------

/**
 * Builds the canonical relative path for the block secret endpoint:
 *   POST business-api/b2b/clients/{clientId}/secrets/{secretId}/block
 * Source: SDD change `credential-api-status-actions`.
 */
export const buildBlockSecretEndpoint = (params: {
  clientId: string;
  secretId: string;
}): string =>
  `business-api/b2b/clients/${params.clientId}/secrets/${params.secretId}/block`;

/**
 * Builds the canonical relative path for the activate secret endpoint:
 *   POST business-api/b2b/clients/{clientId}/secrets/{secretId}/activate
 * Source: SDD change `credential-api-status-actions`.
 */
export const buildActivateSecretEndpoint = (params: {
  clientId: string;
  secretId: string;
}): string =>
  `business-api/b2b/clients/${params.clientId}/secrets/${params.secretId}/activate`;

/**
 * Row action label shown in the credential dropdown menu to trigger the
 * block flow. Mirrors the verb used in the user request
 * (REQ-STA-01 / REQ-STA-04). Source: SDD change
 * `credential-api-status-actions`.
 */
export const CREDENTIAL_API_ROW_ACTION_BLOCK_LABEL = 'Bloquear';

/**
 * Row action label shown in the credential dropdown menu to trigger the
 * activate flow. Mirrors the verb used in the user request
 * (REQ-STA-04). Source: SDD change
 * `credential-api-status-actions`.
 */
export const CREDENTIAL_API_ROW_ACTION_ACTIVATE_LABEL = 'Activar';

// Block credential — confirmation alert copy (SDD change
// `credential-api-status-actions`).
export const CREDENTIAL_API_BLOCK_ALERT_TITLE =
  '¿Seguro que quieres bloquear esta credencial?';
export const CREDENTIAL_API_BLOCK_ALERT_PRIMARY_TEXT = 'Bloquear credencial';
export const CREDENTIAL_API_BLOCK_ALERT_SECONDARY_TEXT = 'No, cancelar';
export const CREDENTIAL_API_BLOCK_SUCCESS_TOAST =
  'Credencial bloqueada con éxito';
export const CREDENTIAL_API_BLOCK_ERROR_TOAST =
  'No pudimos bloquear la credencial. Por favor, intenta de nuevo.';

// Activate credential — confirmation alert copy (SDD change
// `credential-api-status-actions`). The primary text is intentionally
// `Sí, activar` per the launch request (user override of the default
// `Activar credencial` pattern from the delete flow).
export const CREDENTIAL_API_ACTIVATE_ALERT_TITLE =
  '¿Seguro que quieres activar esta credencial?';
export const CREDENTIAL_API_ACTIVATE_ALERT_PRIMARY_TEXT = 'Sí, activar';
export const CREDENTIAL_API_ACTIVATE_ALERT_SECONDARY_TEXT = 'No, cancelar';
export const CREDENTIAL_API_ACTIVATE_SUCCESS_TOAST =
  'Credencial activada con éxito';
export const CREDENTIAL_API_ACTIVATE_ERROR_TOAST =
  'No pudimos activar la credencial. Por favor, intenta de nuevo.';

// -----------------------------------------------------------------------------
// Movements API tab
// Source: SDD change `credential-api-movements`.
// -----------------------------------------------------------------------------

/**
 * Relative path of the transactions endpoint used by
 * `credentialApiService.getTransactions`. Mirrors the canonical
 * `business-api/b2b/...` prefix used by the clients endpoints.
 */
export const CREDENTIAL_API_TRANSACTIONS_ENDPOINT =
  'business-api/b2b/transactions';

/**
 * Builds the canonical relative path for the per-transaction request
 * payload endpoint:
 *   GET business-api/b2b/transactions/{transactionId}/request
 * Source: SDD change `credential-api-movement-detail-request`.
 */
export const buildCredentialApiTransactionRequestEndpoint = (
  transactionId: string | number
): string =>
  `business-api/b2b/transactions/${transactionId}/request`;

/**
 * FontAwesome icon family + name used by the "Ver request" row action.
 * Source: SDD change `credential-api-movement-detail-request`.
 */
export const CREDENTIAL_API_MOVEMENTS_ACTION_VIEW_REQUEST_ICON = 'regular webhook';

/** Modal copy for the "Ver request" dialog. */
export const CREDENTIAL_API_MOVEMENTS_REQUEST_DIALOG_TITLE = 'Request (JSON)';
export const CREDENTIAL_API_MOVEMENTS_REQUEST_DIALOG_CLOSE_BUTTON = 'Cerrar';

/** Error toasts emitted by the "Ver detalle" / "Ver request" handlers. */
export const CREDENTIAL_API_MOVEMENTS_DETAIL_ERROR_TOAST =
  'No pudimos cargar el detalle del movimiento. Por favor, intenta de nuevo.';
export const CREDENTIAL_API_MOVEMENTS_REQUEST_ERROR_TOAST =
  'No pudimos cargar el request del movimiento. Por favor, intenta de nuevo.';

/** Indentation used to pretty-print the raw request JSON payload. */
export const CREDENTIAL_API_MOVEMENTS_REQUEST_JSON_INDENT = 2;

/** Default page size for the movements table. */
export const CREDENTIAL_API_TRANSACTIONS_PAGE_SIZE = 10;

/**
 * Page size options exposed by the "Mostrar" select in the
 * movements filter bar. Mirrors the Movements page convention
 * (10 / 25 / 50). Source: QA alignment with Movements filter UI.
 */
export const CREDENTIAL_API_MOVEMENTS_PAGE_SIZES: ReadonlyArray<{
  title: string;
  value: number;
}> = [
  { title: '10', value: 10 },
  { title: '25', value: 25 },
  { title: '50', value: 50 },
];

/** Floating label of the page-size select (matches Movements page). */
export const CREDENTIAL_API_MOVEMENTS_SHOW_LABEL = 'Mostrar';

/** Default sort for the movements table — createdAt descending. */
export const CREDENTIAL_API_TRANSACTIONS_SORT_BY = 'createdAt';
export const CREDENTIAL_API_TRANSACTIONS_SORT_DIR = 'desc';

/** Operation type option for the movements filter drawer. */
export interface CredentialApiOperationOption {
  value: 'EXCHANGE' | 'P2P' | 'REMITTANCE';
  label: string;
}

/** Operation options shown in the movements filter drawer. */
export const CREDENTIAL_API_OPERATION_OPTIONS: ReadonlyArray<CredentialApiOperationOption> =
  [
    { value: 'EXCHANGE', label: 'Conversión' },
    { value: 'P2P', label: 'Envío a cuenta Global' },
    { value: 'REMITTANCE', label: 'Envío' },
  ];

/**
 * Status options shown in the movements filter drawer. These are the
 * transaction statuses (`PENDING`/`PROCESSING`/`COMPLETED`/`FAILED`)
 * reported by the backend `ApiTransactionStatusEnum` — they are
 * NOT the Credential API credential statuses.
 * Source: SDD decision `credential-api-movements/status-decision`
 * (status correction 2026-06-10).
 */
export const CREDENTIAL_API_MOVEMENTS_STATUS_OPTIONS: ReadonlyArray<{
  value: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
}> = [
  { value: 'PENDING' },
  { value: 'PROCESSING' },
  { value: 'COMPLETED' },
  { value: 'FAILED' },
];

/**
 * Spanish labels for the movements transaction statuses. The
 * movements-specific map keeps the UI copy in one place and is
 * distinct from the credentials status map
 * (`CREDENTIAL_API_STATUS_MAP`).
 */
export const CREDENTIAL_API_MOVEMENTS_STATUS_LABEL_MAP: Readonly<Record<string, string>> = {
  PENDING: 'Pendiente',
  PROCESSING: 'Procesando',
  COMPLETED: 'Completado',
  FAILED: 'Fallido',
};

export const CREDENTIAL_API_TRANSACTION_STATUS_MAP: Readonly<Record<
  string,
  CredentialApiStatusMeta
>> = {
  PENDING: { label: 'Pendiente', tagType: 'info' },
  PROCESSING: { label: 'Procesando', tagType: 'warning' },
  COMPLETED: { label: 'Completado', tagType: 'success' },
  FAILED: { label: 'Fallido', tagType: 'error' },
};

export const CREDENTIAL_API_MOVEMENTS_OPERATION_LABEL_MAP: Readonly<Record<string, string>> = {
  EXCHANGE: 'Conversión',
  P2P: 'Envío a cuenta Global',
  REMITTANCE: 'Envío',
};

/**
 * Maps each Credential API `operationType` to the movement-detail
 * MODAL KIND ("BANK_TRANSFER" / "EXCHANGE" / "P2P") the "Ver detalle"
 * action should open. Both `P2P_PAYMENT` (spec) and `P2P` (actual API
 * row enum) are accepted so the action is offered for real P2P rows.
 * Source: SDD change `credential-api-movement-detail-request`
 * (JD-Judge-A re-review fix 2026-06-12: bridge spec vs row enum).
 */
export const CREDENTIAL_API_MOVEMENT_DETAIL_OPERATION_MAP: Readonly<Record<
  'REMITTANCE' | 'EXCHANGE' | 'P2P_PAYMENT' | 'P2P',
  'BANK_TRANSFER' | 'EXCHANGE' | 'P2P'
>> = {
  REMITTANCE: 'BANK_TRANSFER',
  EXCHANGE: 'EXCHANGE',
  P2P_PAYMENT: 'P2P',
  P2P: 'P2P',
};

/**
 * Maps each Credential API `operationType` to the movement-domain
 * `MovementType` placed on `CredentialApiMovementDetailModel.type`.
 * The promoted P2P/Exchange/Remittance modals expect a movement-type
 * key (`BANK_TRANSFER` / `EXCHANGE` / `P2P_PAYMENT`) — feeding them
 * the bare modal-kind discriminator (`P2P`) caused the P2P modal to
 * hide every sender-only detail (REQ-* / JD-Judge-A re-review).
 * Source: SDD change `credential-api-movement-detail-request`
 * (JD-Judge-A re-review fix 2026-06-12).
 */
export const CREDENTIAL_API_MOVEMENT_DETAIL_MOVEMENT_TYPE_MAP: Readonly<Record<
  'REMITTANCE' | 'EXCHANGE' | 'P2P_PAYMENT' | 'P2P',
  'BANK_TRANSFER' | 'EXCHANGE' | 'P2P_PAYMENT'
>> = {
  REMITTANCE: 'BANK_TRANSFER',
  EXCHANGE: 'EXCHANGE',
  P2P_PAYMENT: 'P2P_PAYMENT',
  P2P: 'P2P_PAYMENT',
};

/** Empty/error/loading copy for the movements table. */
export const CREDENTIAL_API_MOVEMENTS_LOADING_MESSAGE = 'Cargando movimientos…';
export const CREDENTIAL_API_MOVEMENTS_EMPTY_TITLE = 'Aún no tienes movimientos';
export const CREDENTIAL_API_MOVEMENTS_EMPTY_BODY =
  'Cuando ejecutes operaciones con tu API, las verás reflejadas aquí.';
export const CREDENTIAL_API_MOVEMENTS_ERROR_TITLE =
  'No pudimos cargar tus movimientos';
export const CREDENTIAL_API_MOVEMENTS_ERROR_BODY =
  'Por favor, intenta de nuevo en unos momentos.';
export const CREDENTIAL_API_MOVEMENTS_ERROR_RETRY = 'Reintentar';

/** Table column labels. */
export const CREDENTIAL_API_MOVEMENTS_COLUMN_CREDENTIAL = 'Credencial';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_CLIENT_ID = 'Client ID';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_AMOUNT = 'Monto';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_OPERATION = 'Operación';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_RECIPIENT = 'Destinatario';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_CREATED_AT = 'Creación';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_STATUS = 'Estado';
export const CREDENTIAL_API_MOVEMENTS_COLUMN_ACTIONS = 'Acciones';

/**
 * Placeholder rendered when a displayed value is missing, null,
 * undefined, or an empty string. Mirrors the `ContactTable` empty
 * cell fallback pattern (see
 * `src/modules/contacts/components/ContactTable/index.vue`).
 */
export const CREDENTIAL_API_MOVEMENTS_EMPTY_CELL = '---';

/** Drawer copy. */
export const CREDENTIAL_API_MOVEMENTS_FILTERS_TITLE = 'Filtros';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_DESCRIPTION =
  'Puedes aplicar los filtros para acotar los movimientos de tu API.';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_SECTION_CREDENTIALS = 'Credenciales';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_SECTION_OPERATIONS = 'Operaciones';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_SECTION_STATUS = 'Estado';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_SECTION_DATES = 'Rango de fechas';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_DATE_FROM_PLACEHOLDER = 'Inicio';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_DATE_TO_PLACEHOLDER = 'Fin';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_DATE_FORMAT = 'DD-MM-YYYY';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_DATE_VALUE_FORMAT = 'YYYY-MM-DD';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_APPLY = 'Aplicar filtro';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_CLEAR = 'Limpiar filtros';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_SEARCH_PLACEHOLDER = 'Buscar';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_EMPTY =
  'Aún no has generado credenciales';
export const CREDENTIAL_API_MOVEMENTS_FILTERS_NO_CREDENTIALS =
  'No hay credenciales disponibles para filtrar.';

/** Row action labels. */
export const CREDENTIAL_API_MOVEMENTS_ACTION_VIEW_DETAIL = 'Ver detalle';
export const CREDENTIAL_API_MOVEMENTS_ACTION_VIEW_REQUEST = 'Ver Request (JSON)';
export const CREDENTIAL_API_MOVEMENTS_ROW_ACTIONS_ARIA = 'Acciones del movimiento';
